import { Dialog, DialogActions, DialogContent, Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material";
import { TableAlignTopStyle } from "assets/jss/common-styles";
import { LoadingIndicator } from "components/CallServer/LoadingIndicator";
import { RequestErrorReport } from "components/CallServer/RequestErrorReport";
import MuiMessageBox from "components/MuiExtras/MessageBox/MuiMessageBox";
import MuiDialogTitleClose from "components/MuiExtras/MuiDialogTitleClose";
import MuiPagination from "components/MuiExtras/MuiPagination";
import UserFullProfileLink from "components/UserFullProfileLink";
import { Formaters } from "lib/Formarters";
import { isArray } from "lodash";
import { LogRecord, PagedResult, UserShortView } from "models/ApiModels";
import * as React from "react";
import { WithTranslation } from "react-i18next";

///////////////////////////////////////////////////////////////////////////////

export interface ILogTranslated {
    content: string | JSX.Element;
    creationDate: Date;
    creator: UserShortView;
    name: string;
}

interface IProps {
    title?: string;
    onClose: () => void;
}

interface ShowLogState {
    offset: number;
    loading: boolean;
    logData: PagedResult<ILogTranslated>;
    lastError: any;
}

export type GenericLogDialogProps = IProps & WithTranslation;

const MaxRecords = 20;

///////////////////////////////////////////////////////////////////////////////

export abstract class GenericLogDialog<T extends GenericLogDialogProps> extends React.PureComponent<T, ShowLogState> {
    abstract getLog(offset: number, take: number): Promise<PagedResult<LogRecord>>;

    abstract translate(data: LogRecord[]): ILogTranslated[];

    defaultFieldName(key: string, prefix: string) {
        switch (key) {
            case "Created": return this.props.t("DefaultField.Created");
            case "Edited": return this.props.t("DefaultField.Edited");
            case "Deleted": return this.props.t("DefaultField.Deleted");
            case "Recovered": return this.props.t("DefaultField.Recovered");
            case "Name": return this.props.t("DefaultField.Name");
            case "Description": return this.props.t("DefaultField.Description");
            case "Thumbnail": return this.props.t("DefaultField.Thumbnail");
        }
        return this.props.t(`${prefix}.${key}`);
    }

    get modifiedLiteral(): string {
        return this.props.t("Common.ValueModified");
    }

    booleanValueTranslate(v: any): string {
        return this.props.t(v == true ? "Common.BooleanValueTrue" : "Common.BooleanValueFalse");
    }

    nullToNoneTranslate(v: any): string {
        return v == null ? this.props.t("Common.None") : v;
    }

    nullDateToUnlimitTranslate(v: any): string {
        return v == null ? this.props.t("Common.DateUnlimit") : Formaters.localeDate(new Date(v));
    }

    valueFromToTranslate(fromValue: any, toValue: any) {
        return (
            <React.Fragment>
                <div>{this.props.t("Glossary.From")}: {this.nullToNoneTranslate(fromValue)} </div>
                <div>{this.props.t("Glossary.To")}: {this.nullToNoneTranslate(toValue)} </div>
            </React.Fragment>
        );
    }

    dateFromToTranslate(fromValue: any, toValue: any) {
        return (
            <React.Fragment>
                <div>{this.props.t("Glossary.From")}: {this.nullDateToUnlimitTranslate(fromValue)} </div>
                <div>{this.props.t("Glossary.To")}: {this.nullDateToUnlimitTranslate(toValue)} </div>
            </React.Fragment>
        );
    }

    booleanFromToTranslate(fromValue: any, toValue: any) {
        return (
            <React.Fragment>
                <div>{this.props.t("Glossary.From")}: {this.booleanValueTranslate(fromValue)} </div>
                <div>{this.props.t("Glossary.To")}: {this.booleanValueTranslate(toValue)} </div>
            </React.Fragment>
        );
    }

    addOrDeleteDictionaryTranslate(data: PascalCaseKeyValueItem<string, string>[]) {
        return (
            <React.Fragment>
                {data.map((v) => (<div><b>{v.Key}:</b> <span>{v.Value == "add" ? this.props.t("Common.ValueAdded") : this.props.t("Common.ValueDeleted")}</span></div>))}
            </React.Fragment>
        );
    }

    stringListTranslate(data: any) {
        return Array.isArray(data)
            ? <React.Fragment>{(data as string[]).map((v) => (<div>{v}</div>))}</React.Fragment>
            : <React.Fragment>{data}</React.Fragment>;
    }

    newOrRemoveTranslate(fromValue: any, toValue: any) {
        return <React.Fragment>
            {
                fromValue == null
                    ? (<React.Fragment><div>{this.props.t("Common.ValueNewContent")}</div><div>{toValue}</div></React.Fragment>)
                    : toValue == null
                        ? (<React.Fragment><div>{this.props.t("Common.ValueRemoveContent")}</div><div>{fromValue}</div></React.Fragment>)
                        : this.valueFromToTranslate(fromValue, toValue)
            }
        </React.Fragment>;
    }

    constructor(props: T) {
        super(props);
        this.state = { loading: true, logData: null, offset: 0, lastError: null };
    }

    private load(offset: number) {
        this.setState({ loading: true });

        this.getLog(offset, MaxRecords)
            .then((data) => {
                this.setState({
                    loading: false, offset: offset,
                    logData: {
                        items: this.translate(data.items),
                        total: data.total
                    }
                });
            })
            .catch((error) => {
                this.setState({ lastError: error, loading: false, logData: null });
            });
    }

    componentDidMount() {
        this.load(0);
    }

    render() {

        return (
            <Dialog
                open
                fullWidth={true}
                maxWidth="md"
            >
                <MuiDialogTitleClose onClose={() => this.props.onClose()}>
                    {this.props.title || this.props.t("GenericLogDialog.TitleText")}
                </MuiDialogTitleClose>

                <DialogContent >
                    {
                        this.state.loading
                            ? <LoadingIndicator />
                            : this.state.logData == null
                                ? <RequestErrorReport error={this.state.lastError} />
                                : this.state.logData.total == 0
                                    ? <MuiMessageBox variant="info" message={this.props.t("GenericLogDialog.NoData")} />
                                    : (
                                        <div>
                                            <Table>
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell>{this.props.t("GenericLogDialog.Header_Date")}</TableCell>
                                                        <TableCell>{this.props.t("GenericLogDialog.Header_Event")}</TableCell>
                                                        <TableCell>{this.props.t("GenericLogDialog.Header_Info")}</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {
                                                        this.state.logData.items.map((v, index) => {
                                                            return (
                                                                <TableRow key={index} style={TableAlignTopStyle}>
                                                                    <TableCell>
                                                                        <div>
                                                                            {Formaters.localeDate(v.creationDate)} {Formaters.localeTime(v.creationDate)}
                                                                        </div>
                                                                        <div><UserFullProfileLink user={v.creator} /></div>
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        {v.name}
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        <i>{v.content}</i>
                                                                    </TableCell>
                                                                </TableRow>
                                                            );
                                                        })
                                                    }
                                                </TableBody>
                                            </Table>
                                        </div>
                                    )
                    }
                </DialogContent>
                <DialogActions>
                    {
                        this.state.logData != null && this.state.logData.total > 0 &&
                        <MuiPagination
                            limit={MaxRecords}
                            offset={this.state.offset}
                            total={this.state.logData.total}
                            onClick={(e, offset) => this.load(offset)}
                        />
                    }
                </DialogActions>
            </Dialog>
        );
    }
}